import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import L from 'leaflet';
import { setAvoidAreaDrawing } from 'actions';
import { colors } from '../../global/variables';

const { avoidArea } = colors;

const POLYGON_CLOSE_CLICK_MS = 400;

const AvoidAreaDrawing = ({ map }) => {
  const dispatch = useDispatch();
  const { active, tool } = useSelector((state) => state.avoidAreaDrawing);
  const shapeLayerRef = useRef(null);
  const bboxStartRef = useRef(null);
  const polygonPointsRef = useRef([]);
  const lastPolygonClickRef = useRef(0);

  useEffect(() => {
    if (!map || !active) return undefined;

    const clearShapeLayer = () => {
      if (shapeLayerRef.current) {
        map.removeLayer(shapeLayerRef.current);
        shapeLayerRef.current = null;
      }
    };

    const shapeStyle = {
      color: avoidArea,
      weight: 2,
      dashArray: '6,4',
      fillOpacity: 0.08,
      interactive: false,
    };

    // Swallows the native "click" that follows a shape-completing mouseup/dblclick so it can't
    // land on a toolbar button (e.g. Cancel) sitting under the cursor and undo the drawing.
    const suppressNextClick = (domEvent) => {
      domEvent.stopPropagation();
      domEvent.preventDefault();
      document.removeEventListener('click', suppressNextClick, true);
    };
    const scheduleClickSuppression = () => {
      document.addEventListener('click', suppressNextClick, true);
      setTimeout(() => document.removeEventListener('click', suppressNextClick, true), 0);
    };

    const onMouseDown = (e) => {
      if (tool !== 'bbox') return;
      bboxStartRef.current = e.latlng;
      clearShapeLayer();
    };

    // mousemove/mouseup are bound to the document (not the map container) so a drag that ends
    // over the toolbar/collapsed bar sitting on top of the map still completes the shape.
    const onDocumentMouseMove = (domEvent) => {
      if (tool !== 'bbox' || !bboxStartRef.current) return;
      clearShapeLayer();
      const latlng = map.mouseEventToLatLng(domEvent);
      const bounds = L.latLngBounds(bboxStartRef.current, latlng);
      shapeLayerRef.current = L.rectangle(bounds, shapeStyle).addTo(map);
    };

    const onDocumentMouseUp = (domEvent) => {
      if (tool !== 'bbox' || !bboxStartRef.current) return;
      const latlng = map.mouseEventToLatLng(domEvent);
      const bounds = shapeLayerRef.current
        ? shapeLayerRef.current.getBounds()
        : L.latLngBounds(bboxStartRef.current, latlng);
      bboxStartRef.current = null;
      clearShapeLayer();
      if (bounds.getNorth() === bounds.getSouth() || bounds.getEast() === bounds.getWest()) return;
      scheduleClickSuppression();
      dispatch(
        setAvoidAreaDrawing({
          active: false,
          shape: {
            type: 'boundingBox',
            north: bounds.getNorth(),
            south: bounds.getSouth(),
            east: bounds.getEast(),
            west: bounds.getWest(),
          },
        }),
      );
    };

    const onMapClick = (e) => {
      if (tool !== 'polygon') return;
      const now = Date.now();
      const isClosingClick =
        polygonPointsRef.current.length >= 3 &&
        now - lastPolygonClickRef.current < POLYGON_CLOSE_CLICK_MS;
      lastPolygonClickRef.current = now;

      if (isClosingClick) {
        scheduleClickSuppression();
        dispatch(
          setAvoidAreaDrawing({
            active: false,
            shape: {
              type: 'polygon',
              outer: polygonPointsRef.current.map(({ lat, lng }) => ({ lat, lng })),
            },
          }),
        );
        polygonPointsRef.current = [];
        lastPolygonClickRef.current = 0;
        return;
      }

      polygonPointsRef.current = [...polygonPointsRef.current, e.latlng];
      clearShapeLayer();
      shapeLayerRef.current = L.polygon(polygonPointsRef.current, shapeStyle).addTo(map);
    };

    map.dragging.disable();
    map.doubleClickZoom.disable();
    map.on('mousedown', onMouseDown);
    map.on('click', onMapClick);
    document.addEventListener('mousemove', onDocumentMouseMove);
    document.addEventListener('mouseup', onDocumentMouseUp);

    return () => {
      map.off('mousedown', onMouseDown);
      map.off('click', onMapClick);
      document.removeEventListener('mousemove', onDocumentMouseMove);
      document.removeEventListener('mouseup', onDocumentMouseUp);
      clearShapeLayer();
      bboxStartRef.current = null;
      polygonPointsRef.current = [];
      map.dragging.enable();
      map.doubleClickZoom.enable();
    };
  }, [map, active, tool, dispatch]);

  return null;
};

export default AvoidAreaDrawing;
